import { Signal } from '@crown/signal';
import { evaluateGate } from '../context/gate';
import type { RenderContext } from '../context/types';
import type { BoundaryNode, DeferNode, EachNode, ElNode, IfNode, Instruction, TplNode } from '../ir/types';
import { bindDyn, bindEvents, type Disposer, type Teardown, disposeAll } from './bindings';
import { idScope, withIdScope } from '../a11y';
import { decodeRowKey } from '../ir/row-key';
import { END_MARKER } from './complete';
import { InstructionError, rowNeedsMarkers } from '../ir/values';
import { isComment, isElement, isText } from './nodes';
import { ListItems, RowIndex, type RowRecord } from './each';
import { HOLE_END, HOLE_START } from './template';
import {
  adoptSig,
  attachRef,
  boundaryScope,
  createDefer,
  createEach,
  createList,
  fillTemplate,
  installIf,
  swapInClient,
  markIsland,
  namespaceInside,
  type Scope,
  type SigEnd,
} from './mount';

/** Thrown when the server HTML does not match the instruction tree; `path` names the first divergent node. */
export class HydrationMismatch extends Error {
  override readonly name = 'HydrationMismatch';
  constructor(
    message: string,
    readonly path: string,
    readonly node: Node | null,
  ) {
    super(`${message} at ${path}`);
  }
}

export interface HydrateOptions {
  readonly ctx?: RenderContext | null;
  /** Claim `root` itself as the first node, rather than its children — what an island does with its own element. */
  readonly self?: boolean;
}

const consoleSink = (cause: unknown): void => {
  console.error('[@crown/framework] binding failed', cause);
};

function describe(node: Node | null): string {
  if (!node) return 'nothing';
  if (isElement(node)) return `<${node.localName}>`;
  if (isComment(node)) return `<!--${node.data}-->`;
  if (isText(node)) return `text "${node.data.slice(0, 20)}"`;
  return node.nodeName;
}

interface PrefixedComment {
  readonly comment: Comment;
  readonly rest: string;
}

/** Walks the existing children of one parent in step with the instructions. */
class Cursor {
  next: ChildNode | null;

  constructor(readonly parent: Node, from: ChildNode | null = parent.firstChild) {
    this.next = from;
  }

  /** Moves past blank text, which the server's own markup never holds but an author's may. */
  skipBlankText(): void {
    this.skipBlank();
  }

  private skipBlank(): void {
    while (isText(this.next) && this.next.data.trim() === '') this.next = this.next.nextSibling;
  }

  fail(message: string, path: string): never {
    throw new HydrationMismatch(message, path, this.next);
  }

  claimElement(tag: string, path: string): Element {
    this.skipBlank();
    const node = this.next;
    if (!isElement(node) || node.localName !== tag) {
      this.fail(`expected <${tag}>, found ${describe(node)}`, path);
    }
    this.next = node.nextSibling;
    return node;
  }

  claimText(value: string, path: string): Text {
    const node = this.next;
    if (!isText(node)) this.fail(`expected text "${value}", found ${describe(node)}`, path);
    this.next = node.nextSibling;
    return node;
  }

  /** The text node between `c:s` markers, created when the server wrote an empty string. */
  claimSigText(): Text {
    const node = this.next;
    if (isText(node)) {
      this.next = node.nextSibling;
      return node;
    }
    const created = document.createTextNode('');
    this.parent.insertBefore(created, node);
    return created;
  }

  claimComment(data: string, path: string): Comment {
    this.skipBlank();
    const node = this.next;
    if (!isComment(node) || node.data !== data) {
      this.fail(`expected <!--${data}-->, found ${describe(node)}`, path);
    }
    this.next = node.nextSibling;
    return node;
  }

  /** A comment whose data starts with `prefix`; returns the remainder. */
  claimCommentPrefix(prefix: string, path: string): PrefixedComment {
    this.skipBlank();
    const node = this.next;
    if (!isComment(node) || !node.data.startsWith(prefix)) {
      this.fail(`expected <!--${prefix}…-->, found ${describe(node)}`, path);
    }
    this.next = node.nextSibling;
    return { comment: node, rest: node.data.slice(prefix.length) };
  }

  /** Moves past every node up to the comment with `data`, which stays next. */
  skipUntil(data: string, path: string): void {
    while (this.next) {
      if (isComment(this.next) && this.next.data === data) return;
      this.next = this.next.nextSibling;
    }
    this.fail(`expected <!--${data}-->, reached the end`, path);
  }

  skip(count: number): void {
    for (let i = 0; i < count && this.next; i++) this.next = this.next.nextSibling;
  }
}

function hydrateElement(node: ElNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  const element = cursor.claimElement(node.tag, path);
  for (const dyn of node.dyn) bindDyn(element, dyn, disposers, scope.sink);
  bindEvents(element, node.on, disposers);
  if (node.island) markIsland(element, node, null);
  const inner = new Cursor(element);
  // Content a branch creates here later must be created in the namespace the server's element is in.
  const ns = namespaceInside(element);
  const childScope: Scope = ns === scope.ns ? scope : ns === undefined ? { ctx: scope.ctx, sink: scope.sink } : { ctx: scope.ctx, sink: scope.sink, ns };
  node.children.forEach((child, i) => hydrateNode(child, inner, childScope, disposers, `${path}/${i}`));
  if (node.ref) attachRef(element, node.ref, disposers);
  if (node.island || node.attrs['data-c-element'] !== undefined) cursor.claimComment(END_MARKER, path);
}

function hydrateIf(node: IfNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  cursor.claimComment('c:if', path);
  const active = Signal.subtle.untrack(() => node.cond());
  const attach = installIf(node, scope, disposers, Boolean(active));
  const branch = active ? node.body() : node.otherwise();
  const branchDisposers: Teardown[] = [];
  const branchPath = `${path}/${active ? 'then' : 'else'}`;
  branch.forEach((child, i) => hydrateNode(child, cursor, scope, branchDisposers, `${branchPath}/${i}`));
  attach(cursor.claimComment('/c:if', path), branchDisposers);
}

function hydrateEach(node: EachNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  const start = cursor.claimComment('c:each', path);
  const rows = new Map<string, RowRecord>();
  const list = new ListItems(node.tracksItem === true);
  node.items().forEach((item, i) => {
    const key = node.key(item, i);
    // Refused here as a mount and every reconcile refuse it: the server writes
    // a duplicate in production, and a second row under one key would push the
    // first out of the map with its nodes and bindings still on the page.
    if (rows.has(key)) throw new InstructionError(`each() produced the key "${key}" twice`);
    const rowPath = `${path}/row(${key})`;
    const index = new RowIndex(i, item, list);
    const rowDisposers: Teardown[] = [];
    // SAFETY: the row's item reader yields the items of this list, which are T.
    const children = node.row(item, index.read, index.readItem as () => typeof item);
    const dispose = rowDisposers;
    if (rowNeedsMarkers(children)) {
      const { comment: rowStart, rest } = cursor.claimCommentPrefix('c:row:', rowPath);
      if (decodeRowKey(rest) !== key) cursor.fail(`expected row "${key}", found row "${rest}"`, rowPath);
      children.forEach((child, j) => hydrateNode(child, cursor, scope, rowDisposers, `${rowPath}/${j}`));
      const rowEnd = cursor.claimComment('/c:row', rowPath);
      rows.set(key, { first: rowStart, last: rowEnd, disposers: dispose, index });
      return;
    }
    // Found by position: the row is whatever its children claimed.
    cursor.skipBlankText();
    const first = cursor.next;
    children.forEach((child, j) => hydrateNode(child, cursor, scope, rowDisposers, `${rowPath}/${j}`));
    const last = cursor.next ? cursor.next.previousSibling : cursor.parent.lastChild;
    const claimed = first !== cursor.next;
    rows.set(key, { first: claimed ? first : null, last: claimed ? last : null, disposers: dispose, index });
  });
  const end = cursor.claimComment('/c:each', path);
  createEach(node, scope, disposers, start, end, rows, list);
}

/** Hydrates a deferred region, pending or already swapped (start marker `c:defer:ID:done`). */
function hydrateDefer(node: DeferNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  const { comment: start, rest } = cursor.claimCommentPrefix('c:defer:', path);
  const swapped = rest.endsWith(':done');
  const id = swapped ? rest.slice(0, -':done'.length) : rest;
  const inner = new Cursor(cursor.parent, cursor.next);
  const innerDisposers: Teardown[] = [];
  disposers.push(() => disposeAll(innerDisposers));
  if (swapped) {
    cursor.skipUntil(`/c:defer:${id}`, path);
    const end = cursor.claimComment(`/c:defer:${id}`, path);
    hydrateResolved(node, scope, innerDisposers, start, end, `${path}/resolved`);
    return;
  }
  node.pending.forEach((child, i) => hydrateNode(child, inner, scope, innerDisposers, `${path}/pending/${i}`));
  cursor.next = inner.next;
  const end = cursor.claimComment(`/c:defer:${id}`, path);
  // The client resolves this region itself now; renaming the marker makes a
  // late server swap a no-op, so it cannot replace live, bound content.
  start.data = `c:defer:${id}:client`;
  createDefer(node, scope, disposers, start, end, []);
}

/** The resolved instruction is only known by resolving again; its result hydrates against the swapped content, or replaces it on a mismatch. */
function hydrateResolved(
  node: DeferNode,
  scope: Scope,
  disposers: Teardown[],
  start: Comment,
  end: Comment,
  path: string,
): void {
  let live = true;
  disposers.push(() => {
    live = false;
  });
  node
    .resolve()
    .then((resolved) => {
      if (!live) return;
      const fresh: Teardown[] = [];
      const cursor = new Cursor(start.parentNode ?? document, start.nextSibling);
      try {
        resolved.forEach((child, i) => hydrateNode(child, cursor, scope, fresh, `${path}/${i}`));
        disposers.push(() => disposeAll(fresh));
      } catch (error) {
        if (!(error instanceof HydrationMismatch)) throw error;
        disposeAll(fresh);
        console.error('[@crown/framework] deferred content did not match; re-rendering', error);
        const nodes = createList(resolved, scope, fresh);
        disposers.push(() => disposeAll(fresh));
        while (start.nextSibling && start.nextSibling !== end) start.nextSibling.remove();
        const parent = end.parentNode;
        if (parent) for (const created of nodes) parent.insertBefore(created, end);
      }
    })
    .catch((cause: unknown) => {
      if (live) scope.sink(cause);
    });
}

function hydrateBoundary(node: BoundaryNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  cursor.claimComment('c:b', path);
  const probe = new Cursor(cursor.parent, cursor.next);
  probe.skipUntil('/c:b', path);
  const end = probe.next;
  if (!isComment(end)) cursor.fail('expected <!--/c:b-->', path);
  const boundary = boundaryScope(node, scope, disposers, end);
  try {
    node.children.forEach((child, i) =>
      hydrateNode(child, cursor, boundary.inner, boundary.innerDisposers, `${path}/${i}`),
    );
  } catch (error) {
    if (error instanceof HydrationMismatch) throw error;
    boundary.showFallback(error);
  }
  cursor.next = end;
  cursor.claimComment('/c:b', path);
}

function hydrateNode(node: Instruction, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  switch (node.t) {
    case 'el':
      if (evaluateGate(node.gate, scope.ctx)) hydrateElement(node, cursor, scope, disposers, `${path}:${node.tag}`);
      return;
    case 'text':
      if (node.value.trim() !== '') cursor.claimText(node.value, `${path}:text`);
      return;
    case 'sig': {
      cursor.claimComment('c:s', `${path}:sig`);
      const endRef: SigEnd = { current: null };
      adoptSig(node, scope, disposers, endRef, (_value, tree, inner) => {
        if (!tree) return cursor.claimSigText();
        tree.forEach((child, i) => hydrateNode(child, cursor, scope, inner, `${path}:sig/${i}`));
        return null;
      });
      endRef.current = cursor.claimComment('/c:s', `${path}:sig`);
      return;
    }
    case 'if':
      if (evaluateGate(node.gate, scope.ctx)) hydrateIf(node, cursor, scope, disposers, `${path}:if`);
      return;
    case 'each':
      if (evaluateGate(node.gate, scope.ctx)) hydrateEach(node, cursor, scope, disposers, `${path}:each`);
      return;
    case 'raw': {
      const template = document.createElement('template');
      template.innerHTML = node.html;
      cursor.skip(template.content.childNodes.length);
      return;
    }
    case 'static':
      node.children.forEach((child, i) => hydrateNode(child, cursor, scope, disposers, `${path}:static/${i}`));
      return;
    case 'defer':
      if (evaluateGate(node.gate, scope.ctx)) hydrateDefer(node, cursor, scope, disposers, `${path}:defer`);
      return;
    case 'boundary':
      hydrateBoundary(node, cursor, scope, disposers, `${path}:boundary`);
      return;
    case 'region':
      if (evaluateGate(node.gate, scope.ctx)) cursor.claimElement('div', `${path}:region`);
      return;
    case 'fragment':
      node.children.forEach((child, i) => hydrateNode(child, cursor, scope, disposers, `${path}/${i}`));
      return;
    case 'dom':
      cursor.fail('a dom node cannot be hydrated', `${path}:dom`);
      return;
    case 'tpl':
      hydrateTemplate(node, cursor, scope, disposers, `${path}:${node.template.tag}`);
      return;
    case 'client': {
      cursor.claimComment('c:client', `${path}:client`);
      const fallback: Teardown[] = [];
      node.fallback.forEach((child, i) => hydrateNode(child, cursor, scope, fallback, `${path}:client/${i}`));
      const end = cursor.claimComment('/c:client', `${path}:client`);
      disposers.push(() => disposeAll(fallback));
      swapInClient(node, scope, disposers, end, fallback);
      return;
    }
    case 'server':
      // Adopted as the server wrote it: nothing inside is bound.
      cursor.claimComment('c:server', `${path}:server`);
      cursor.skipUntil('/c:server', `${path}:server`);
      cursor.claimComment('/c:server', `${path}:server`);
  }
}

/** Claims the template's element and walks its slots on the server's copy. */
function hydrateTemplate(node: TplNode, cursor: Cursor, scope: Scope, disposers: Teardown[], path: string): void {
  const root = cursor.claimElement(node.template.tag, path);
  fillTemplate(root, node, scope, disposers, true, (start, end, children, inner, holeDisposers) => {
    const holeCursor = new Cursor(start.parentNode ?? root, start.nextSibling);
    const only = children.length === 1 ? children[0] : undefined;
    if (only?.t === 'sig') {
      adoptSig(
        only,
        inner,
        holeDisposers,
        { current: end },
        (_value, tree, sigDisposers) => {
          if (!tree) return holeCursor.claimSigText();
          tree.forEach((child, i) => hydrateNode(child, holeCursor, inner, sigDisposers, `${path}:hole/${i}`));
          return null;
        },
        HOLE_START,
      );
    } else {
      children.forEach((child, i) => hydrateNode(child, holeCursor, inner, holeDisposers, `${path}:hole/${i}`));
    }
    if (holeCursor.next !== end) {
      // Only blank text may be left: the server wrote nothing else here.
      holeCursor.claimComment(HOLE_END, `${path}:hole`);
    }
  });
}

function cursorAt(element: Element): Cursor {
  const parent = element.parentNode;
  if (!parent) throw new HydrationMismatch('the root has no parent to claim it from', 'root', element);
  return new Cursor(parent, element);
}

/** Attaches bindings to server-rendered HTML without creating elements. */
export function hydrate(
  node: Instruction,
  root: Element | Document | DocumentFragment,
  options: HydrateOptions = {},
): Disposer {
  const disposers: Teardown[] = [];
  // The root's own children are in its namespace; a claimed root is in its parent's.
  const home = options.self ? root.parentNode : root;
  const ns = home ? namespaceInside(home) : undefined;
  const scope: Scope = ns === undefined ? { ctx: options.ctx ?? null, sink: consoleSink } : { ctx: options.ctx ?? null, sink: consoleSink, ns };
  const cursor = options.self && isElement(root) ? cursorAt(root) : new Cursor(root);
  try {
    // Ids are numbered in document order, as the server numbered them.
    withIdScope(idScope(), () => hydrateNode(node, cursor, scope, disposers, 'root'));
  } catch (error) {
    disposeAll(disposers);
    throw error;
  }
  return () => disposeAll(disposers);
}
